import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { canApprovePto } from '@/lib/permissions';
import { formatCurrency, todayIsoDate } from '@/lib/formatters';
import type {
  EmployeeDocumentCategory,
  EmployeeEmploymentStatus,
  EmployeeEmploymentType,
  EmployeeGoalStatus,
  EmployeePayReason,
  EmployeePayType,
  EmployeeTimelineKind,
  Inserts,
  Tables,
} from '@/types/database';
import {
  EMPLOYEE_DOCUMENT_MAX_BYTES,
  EMPLOYEE_DOCUMENTS_BUCKET,
  friendlyHrError,
  timelineKindForPayReason,
} from './model';

export type EmployeeHrInfo = Tables<'employee_hr_info'>;

export const hrInfoKeys = {
  all: ['employee-hr-info'] as const,
  mine: (profileId: string) => [...hrInfoKeys.all, 'mine', profileId] as const,
  org: () => [...hrInfoKeys.all, 'org'] as const,
};

/** Signed-in user's HR start date + pay rate (for My PTO cycle / payout). */
export function useMyHrInfo() {
  const { profile } = useAuth();
  return useQuery({
    queryKey: hrInfoKeys.mine(profile?.id ?? ''),
    enabled: !!profile?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employee_hr_info')
        .select('*')
        .eq('profile_id', profile!.id)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as EmployeeHrInfo | null;
    },
  });
}

/** Every employee's HR info in the org — approvers only. */
export function useAllHrInfo() {
  const { profile, role } = useAuth();
  return useQuery({
    queryKey: hrInfoKeys.org(),
    enabled: !!profile && canApprovePto(role),
    queryFn: async () => {
      const { data, error } = await supabase.from('employee_hr_info').select('*');
      if (error) throw error;
      return (data ?? []) as EmployeeHrInfo[];
    },
  });
}

export interface UpsertHrInfoInput {
  profile_id: string;
  start_date: string | null;
  pay_rate_cents: number | null;
  pay_type: EmployeePayType;
}

/** Create or update an employee's start date + pay rate (approvers). */
export function useUpsertHrInfo() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: UpsertHrInfoInput) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const payload: Inserts<'employee_hr_info'> = {
        profile_id: input.profile_id,
        organization_id: profile.organization_id,
        start_date: input.start_date,
        pay_rate_cents: input.pay_rate_cents,
        pay_type: input.pay_type,
        updated_by: profile.id,
      };
      const { data, error } = await supabase
        .from('employee_hr_info')
        .upsert(payload, { onConflict: 'profile_id' })
        .select('*')
        .single();
      if (error) throw error;
      return data as EmployeeHrInfo;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: hrInfoKeys.all });
    },
  });
}

export type EmployeeHrPrivate = Tables<'employee_hr_private'>;
export type EmployeeCompensation = Tables<'employee_compensation'>;
export type EmployeeGoal = Tables<'employee_goals'>;
export type EmployeeDocument = Tables<'employee_documents'>;
export type EmployeeTimelineEvent = Tables<'employee_timeline'>;

const privateKeys = {
  all: ['employee-hr-private'] as const,
  one: (profileId: string) => [...privateKeys.all, profileId] as const,
};
const compensationKeys = {
  all: ['employee-compensation'] as const,
  one: (profileId: string) => [...compensationKeys.all, profileId] as const,
};
const goalKeys = {
  all: ['employee-goals'] as const,
  one: (profileId: string) => [...goalKeys.all, profileId] as const,
};
const documentKeys = {
  all: ['employee-documents'] as const,
  one: (profileId: string) => [...documentKeys.all, profileId] as const,
};
const timelineKeys = {
  all: ['employee-timeline'] as const,
  one: (profileId: string) => [...timelineKeys.all, profileId] as const,
};

export interface EmployeeProfileFields {
  full_name: string;
  job_title: string;
  start_date: string;
  date_of_birth: string;
  phone: string;
  personal_email: string;
  pronouns: string;
  bio: string;
  timezone: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  emergency_contact_relationship: string;
}

export interface EmployeeHrFields {
  preferred_name: string;
  position: string;
  department: string;
  employment_type: EmployeeEmploymentType | '';
  employment_status: EmployeeEmploymentStatus;
  manager_id: string;
  work_location: string;
  shirt_size: string;
  allergies: string;
  dietary_notes: string;
  favorite_snack: string;
  coffee_order: string;
  languages: string;
  interests: string;
  celebration_notes: string;
  celebrate_birthday: boolean;
  secondary_emergency_name: string;
  secondary_emergency_phone: string;
  secondary_emergency_relationship: string;
  hr_notes: string;
}

export type EmployeeDraft = EmployeeProfileFields & EmployeeHrFields;

export interface SaveEmployeeInput {
  profileId: string;
  profile: EmployeeProfileFields;
  hr: EmployeeHrFields;
}

/** Approver save of identity, care details, and confidential notes. Pay is untouched. */
export function useSaveEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveEmployeeInput) => {
      const { data, error } = await supabase.rpc(
        'hr_save_employee' as never,
        {
          p_profile_id: input.profileId,
          p_profile: input.profile,
          p_hr: input.hr,
        } as never,
      );
      if (error) throw new Error(friendlyHrError(error));
      return data as EmployeeHrInfo;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: hrInfoKeys.all });
      void qc.invalidateQueries({ queryKey: ['profiles'] });
      void qc.invalidateQueries({ queryKey: privateKeys.one(vars.profileId) });
    },
  });
}

export function useEmployeePrivateNotes(profileId: string | null) {
  const { profile, role } = useAuth();
  return useQuery({
    queryKey: privateKeys.one(profileId ?? ''),
    enabled: !!profile && !!profileId && canApprovePto(role),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employee_hr_private')
        .select('*')
        .eq('profile_id', profileId!)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as EmployeeHrPrivate | null;
    },
  });
}

export function useEmployeeCompensation(profileId: string | null) {
  const { profile, role } = useAuth();
  return useQuery({
    queryKey: compensationKeys.one(profileId ?? ''),
    enabled: !!profile && !!profileId && canApprovePto(role),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employee_compensation')
        .select('*')
        .eq('profile_id', profileId!)
        .order('effective_date', { ascending: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as EmployeeCompensation[];
    },
  });
}

export function useRecordCompensation() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      profileId: string;
      effectiveDate: string;
      payType: EmployeePayType;
      payRateCents: number;
      reason: EmployeePayReason;
      note: string | null;
    }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const payload: Inserts<'employee_compensation'> = {
        organization_id: profile.organization_id,
        profile_id: input.profileId,
        effective_date: input.effectiveDate,
        pay_type: input.payType,
        pay_rate_cents: input.payRateCents,
        reason: input.reason,
        note: input.note,
        created_by: profile.id,
      };
      const { data, error } = await supabase
        .from('employee_compensation')
        .insert(payload)
        .select('*')
        .single();
      if (error) throw error;

      const rate = formatCurrency(input.payRateCents);
      const unit = input.payType === 'hourly' ? '/hr' : '/yr';
      const title =
        input.reason === 'hire'
          ? 'Starting rate recorded'
          : input.reason === 'raise'
            ? 'Raise'
            : input.reason === 'promotion'
              ? 'Promotion'
              : input.reason === 'adjustment'
                ? 'Pay adjustment'
                : 'Pay correction';
      const timeline: Inserts<'employee_timeline'> = {
        organization_id: profile.organization_id,
        profile_id: input.profileId,
        occurred_on: input.effectiveDate,
        kind: timelineKindForPayReason(input.reason),
        title,
        body: [`${rate}${unit}`, input.note].filter(Boolean).join(' — '),
        created_by: profile.id,
      };
      // The rate is already saved. A timeline miss should not invite a second entry.
      await supabase.from('employee_timeline').insert(timeline);
      return data as EmployeeCompensation;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: compensationKeys.one(vars.profileId) });
      void qc.invalidateQueries({ queryKey: hrInfoKeys.all });
      void qc.invalidateQueries({ queryKey: timelineKeys.one(vars.profileId) });
    },
  });
}

export function useEmployeeGoals(profileId: string | null) {
  const { profile, role } = useAuth();
  return useQuery({
    queryKey: goalKeys.one(profileId ?? ''),
    enabled: !!profile && !!profileId && canApprovePto(role),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employee_goals')
        .select('*')
        .eq('profile_id', profileId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as EmployeeGoal[];
    },
  });
}

export function useCreateGoal() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      profileId: string;
      title: string;
      description: string | null;
      targetDate: string | null;
    }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const payload: Inserts<'employee_goals'> = {
        organization_id: profile.organization_id,
        profile_id: input.profileId,
        title: input.title,
        description: input.description,
        target_date: input.targetDate,
        created_by: profile.id,
      };
      const { data, error } = await supabase
        .from('employee_goals')
        .insert(payload)
        .select('*')
        .single();
      if (error) throw error;
      return data as EmployeeGoal;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: goalKeys.one(vars.profileId) });
    },
  });
}

export function useUpdateGoalStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; profileId: string; status: EmployeeGoalStatus }) => {
      const { error } = await supabase
        .from('employee_goals')
        .update({ status: input.status })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: goalKeys.one(vars.profileId) });
    },
  });
}

export function useDeleteGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; profileId: string }) => {
      const { error } = await supabase.from('employee_goals').delete().eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: goalKeys.one(vars.profileId) });
    },
  });
}

export function useEmployeeDocuments(profileId: string | null) {
  const { profile, role } = useAuth();
  return useQuery({
    queryKey: documentKeys.one(profileId ?? ''),
    enabled: !!profile && !!profileId && canApprovePto(role),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employee_documents')
        .select('*')
        .eq('profile_id', profileId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as EmployeeDocument[];
    },
  });
}

function buildDocumentPath(organizationId: string, profileId: string, filename: string): string {
  const stamp = Date.now();
  const cleaned = filename.replace(/[^a-zA-Z0-9._-]+/g, '_');
  return `${organizationId}/${profileId}/${stamp}-${cleaned}`;
}

export function useUploadEmployeeDocument() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      profileId: string;
      file: File;
      category: EmployeeDocumentCategory;
      title: string;
      notes: string | null;
    }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      if (input.file.size > EMPLOYEE_DOCUMENT_MAX_BYTES) {
        throw new Error('That file is over 25 MB.');
      }
      const path = buildDocumentPath(profile.organization_id, input.profileId, input.file.name);
      const { error: uploadErr } = await supabase.storage
        .from(EMPLOYEE_DOCUMENTS_BUCKET)
        .upload(path, input.file, {
          upsert: false,
          cacheControl: '3600',
          contentType: input.file.type || undefined,
        });
      if (uploadErr) throw uploadErr;

      const payload: Inserts<'employee_documents'> = {
        organization_id: profile.organization_id,
        profile_id: input.profileId,
        category: input.category,
        title: input.title.trim() || input.file.name,
        storage_path: path,
        file_name: input.file.name,
        mime_type: input.file.type || null,
        byte_size: input.file.size,
        notes: input.notes,
        uploaded_by: profile.id,
      };
      const { data, error } = await supabase
        .from('employee_documents')
        .insert(payload)
        .select('*')
        .single();
      if (error) {
        await supabase.storage.from(EMPLOYEE_DOCUMENTS_BUCKET).remove([path]);
        throw error;
      }

      const timeline: Inserts<'employee_timeline'> = {
        organization_id: profile.organization_id,
        profile_id: input.profileId,
        occurred_on: todayIsoDate(),
        kind: 'document',
        title: `Document added — ${payload.title}`,
        body: input.file.name,
        created_by: profile.id,
      };
      await supabase.from('employee_timeline').insert(timeline);
      return data as EmployeeDocument;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: documentKeys.one(vars.profileId) });
      void qc.invalidateQueries({ queryKey: timelineKeys.one(vars.profileId) });
    },
  });
}

export function useDeleteEmployeeDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; profileId: string; storagePath: string }) => {
      const { error } = await supabase.from('employee_documents').delete().eq('id', input.id);
      if (error) throw error;
      await supabase.storage.from(EMPLOYEE_DOCUMENTS_BUCKET).remove([input.storagePath]);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: documentKeys.one(vars.profileId) });
    },
  });
}

export async function openEmployeeDocument(path: string) {
  const { data, error } = await supabase.storage
    .from(EMPLOYEE_DOCUMENTS_BUCKET)
    .createSignedUrl(path, 60 * 5);
  if (error) throw error;
  window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
}

export function useEmployeeTimeline(profileId: string | null) {
  const { profile, role } = useAuth();
  return useQuery({
    queryKey: timelineKeys.one(profileId ?? ''),
    enabled: !!profile && !!profileId && canApprovePto(role),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employee_timeline')
        .select('*')
        .eq('profile_id', profileId!)
        .order('occurred_on', { ascending: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as EmployeeTimelineEvent[];
    },
  });
}

export function useAddTimelineEvent() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      profileId: string;
      occurredOn: string;
      kind: EmployeeTimelineKind;
      title: string;
      body: string | null;
    }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const payload: Inserts<'employee_timeline'> = {
        organization_id: profile.organization_id,
        profile_id: input.profileId,
        occurred_on: input.occurredOn,
        kind: input.kind,
        title: input.title,
        body: input.body,
        created_by: profile.id,
      };
      const { data, error } = await supabase
        .from('employee_timeline')
        .insert(payload)
        .select('*')
        .single();
      if (error) throw error;
      return data as EmployeeTimelineEvent;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: timelineKeys.one(vars.profileId) });
    },
  });
}

export function useDeleteTimelineEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; profileId: string }) => {
      const { error } = await supabase.from('employee_timeline').delete().eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: timelineKeys.one(vars.profileId) });
    },
  });
}
